import * as XLSX from "xlsx";

/* ─────────────────────────────────────────────────────────────
   COLUMN GROUPS
   Each group has a name, a hex accent color, and its columns.
   Order here = order in the spreadsheet.
───────────────────────────────────────────────────────────── */
export const COLUMN_GROUPS = [
  {
    group: "Identity",
    color: "1E3A5F",
    textColor: "FFFFFF",
    cols: [
      { key: "account",        label: "Account" },
      { key: "sub_account",    label: "Sub Account" },
      { key: "end_client",     label: "End Client" },
      { key: "role_title",     label: "Role Title" },
      { key: "recruiter_name", label: "Recruiter Name" },
    ],
  },
  {
    group: "Demand",
    color: "2D4A7A",
    textColor: "FFFFFF",
    cols: [
      { key: "openings", label: "Openings" },
    ],
  },
  {
    group: "Sourcing & Assessment",
    color: "4F46E5",
    textColor: "FFFFFF",
    cols: [
      { key: "profile_sourced",  label: "Profiles Sourced" },
      { key: "assessed",         label: "Assessed" },
      { key: "assessed_cleared", label: "Assessed & Cleared" },
    ],
  },
  {
    group: "Sent to Client",
    color: "0EA5E9",
    textColor: "FFFFFF",
    cols: [
      { key: "sent_to_client",          label: "Sent to Client" },
      { key: "duplicate",               label: "Duplicate Profile" },
      { key: "change_in_requirement",   label: "Change in Requirement" },
      { key: "position_on_hold_closed", label: "Position on Hold / Closed" },
      { key: "profile_reshared",        label: "Profile Reshared" },
    ],
  },
  {
    group: "Screening",
    color: "D97706",
    textColor: "FFFFFF",
    cols: [
      { key: "yet_to_be_shortlisted",   label: "Yet to be Shortlisted" },
      { key: "screen_reject",           label: "Screen Reject" },
      { key: "pre_interview_back_out",  label: "Pre-Interview Back Out" },
      { key: "post_interview_back_out", label: "Post Interview Back Out" },
      { key: "screen_select",           label: "Screen Select - Yet to be Interviewed (R1)" },
      { key: "screen_select_funnel",    label: "Screen Select (Funnel)" },
    ],
  },
  {
    group: "Round 1",
    color: "7C3AED",
    textColor: "FFFFFF",
    cols: [
      { key: "interview_scheduled_r1",       label: "Interview Scheduled" },
      { key: "during_interview_back_out_r1", label: "During Interview Back Out" },
      { key: "feedback_pending_r1",          label: "Feedback Pending" },
      { key: "rejected_r1",                  label: "Rejected" },
      { key: "selected_r1_funnel",           label: "Selected (Funnel)" },
    ],
  },
  {
    group: "Round 2",
    color: "2563EB",
    textColor: "FFFFFF",
    cols: [
      { key: "yet_to_be_interviewed_r2",     label: "Yet to be Interviewed" },
      { key: "interview_scheduled_r2",       label: "Interview Scheduled" },
      { key: "during_interview_back_out_r2", label: "During Interview Back Out" },
      { key: "feedback_pending_r2",          label: "Feedback Pending" },
      { key: "rejected_r2",                  label: "Rejected" },
      { key: "selected_r2_funnel",           label: "Selected (Funnel)" },
    ],
  },
  {
    group: "Round 3",
    color: "0891B2",
    textColor: "FFFFFF",
    cols: [
      { key: "yet_to_be_interviewed_r3",     label: "Yet to be Interviewed" },
      { key: "interview_scheduled_r3",       label: "Interview Scheduled" },
      { key: "during_interview_back_out_r3", label: "During Interview Back Out" },
      { key: "feedback_pending_r3",          label: "Feedback Pending" },
      { key: "rejected_r3",                  label: "Rejected" },
      { key: "selected_r3_funnel",           label: "Selected (Funnel)" },
    ],
  },
  {
    group: "Round 4",
    color: "059669",
    textColor: "FFFFFF",
    cols: [
      { key: "yet_to_be_interviewed_r4",     label: "Yet to be Interviewed" },
      { key: "interview_scheduled_r4",       label: "Interview Scheduled" },
      { key: "during_interview_back_out_r4", label: "During Interview Back Out" },
      { key: "feedback_pending_r4",          label: "Feedback Pending" },
      { key: "rejected_r4",                  label: "Rejected" },
      { key: "selected_r4_funnel",           label: "Selected (Funnel)" },
    ],
  },
  {
    group: "Final Stage",
    color: "065F46",
    textColor: "FFFFFF",
    cols: [
      { key: "final_select",        label: "Final Select" },
      { key: "final_reject",        label: "Final Reject" },
      { key: "pre_offer_back_out",  label: "Pre Offer Back Out" },
      { key: "final_select_funnel", label: "Final Select (Funnel)" },
    ],
  },
  {
    group: "Offer Stage",
    color: "C2410C",
    textColor: "FFFFFF",
    cols: [
      { key: "offer_released",           label: "Offer Released" },
      { key: "offer_yet_to_be_released", label: "Yet to be Released" },
      { key: "post_offer_back_out",      label: "Post Offer Back Out" },
      { key: "offer_released_funnel",    label: "Offer Released (Funnel)" },
    ],
  },
  {
    group: "Joining",
    color: "15803D",
    textColor: "FFFFFF",
    cols: [
      { key: "offer_accepted_yet_to_join", label: "Offer Accepted - Yet to Join" },
      { key: "offer_accepted_joined",      label: "Offer Accepted - Joined" },
      { key: "offer_accepted_not_joined",  label: "Offer Accepted - Not Joined" },
      { key: "offer_rejected_not_joined",  label: "Offer Rejected - Not Joined" },
      { key: "joined_funnel",              label: "Joined (Funnel)" },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */

/** Lighten a hex color by blending toward white at `amount` (0–1) */
function lightenHex(hex, amount = 0.45) {
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return [lr, lg, lb]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

/** Convert 0-based col index to Excel letter (A, B, …, AA, AB, …) */
function colLetter(idx) {
  let letter = "";
  let n = idx;
  while (n >= 0) {
    letter = String.fromCharCode((n % 26) + 65) + letter;
    n = Math.floor(n / 26) - 1;
  }
  return letter;
}

/* ─────────────────────────────────────────────────────────────
   MAIN EXPORT FUNCTION
   Call this instead of exportToCSV:
     exportToXLSX(filteredData, `recruitment_${from}_to_${to}.xlsx`)
───────────────────────────────────────────────────────────── */
export function exportToXLSX(rows, filename = "recruitment_export.xlsx") {
  // Flatten column list
  const flatCols = COLUMN_GROUPS.flatMap((g) =>
    g.cols.map((c) => ({ ...c, group: g.group, color: g.color, textColor: g.textColor }))
  );

  const totalCols = flatCols.length;

  /* ── Build worksheet data array ──
     aoa = array of arrays; we'll set styles via cell objects */
  const aoa = [];

  // Row 0: group header labels (one per column — merges applied below)
  const groupRow = flatCols.map((c) => c.group);
  aoa.push(groupRow);

  // Row 1: individual column labels
  aoa.push(flatCols.map((c) => c.label));

  // Rows 2+: data
  rows.forEach((row) => {
    aoa.push(flatCols.map((c) => row[c.key] ?? ""));
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  /* ── Merges for group header row ── */
  const merges = [];
  let colCursor = 0;
  COLUMN_GROUPS.forEach((g) => {
    const span = g.cols.length;
    if (span > 1) {
      merges.push({
        s: { r: 0, c: colCursor },
        e: { r: 0, c: colCursor + span - 1 },
      });
    }
    colCursor += span;
  });
  ws["!merges"] = merges;

  /* ── Column widths ── */
  const identityKeys = new Set(COLUMN_GROUPS[0].cols.map((c) => c.key));
  ws["!cols"] = flatCols.map((c) => ({
    wch: identityKeys.has(c.key) ? 24 : c.label.length > 20 ? 18 : 14,
  }));

  /* ── Row heights ── */
  ws["!rows"] = [
    { hpt: 20 }, // group header row
    { hpt: 36 }, // column label row (taller for wrap)
  ];

  /* ── Cell styles ── */
  const borderStyle = {
    top:    { style: "thin", color: { rgb: "D1D5DB" } },
    bottom: { style: "thin", color: { rgb: "D1D5DB" } },
    left:   { style: "thin", color: { rgb: "D1D5DB" } },
    right:  { style: "thin", color: { rgb: "D1D5DB" } },
  };

  // Style group header row (row 0)
  colCursor = 0;
  COLUMN_GROUPS.forEach((g) => {
    g.cols.forEach((_, i) => {
      const cellRef = `${colLetter(colCursor + i)}1`;
      if (!ws[cellRef]) ws[cellRef] = { v: g.group, t: "s" };
      ws[cellRef].s = {
        font:      { bold: true, sz: 11, color: { rgb: g.textColor }, name: "Arial" },
        fill:      { fgColor: { rgb: g.color }, patternType: "solid" },
        alignment: { horizontal: "center", vertical: "center" },
        border:    borderStyle,
      };
    });
    colCursor += g.cols.length;
  });

  // Style column label row (row 1) and data rows
  flatCols.forEach((col, ci) => {
    const lightColor = lightenHex(col.color, 0.45);
    const isIdentity = identityKeys.has(col.key);

    // Header cell (row 2 in Excel = index 1 in aoa)
    const headerRef = `${colLetter(ci)}2`;
    if (!ws[headerRef]) ws[headerRef] = { v: col.label, t: "s" };
    ws[headerRef].s = {
      font:      { bold: true, sz: 9, color: { rgb: "1F2937" }, name: "Arial" },
      fill:      { fgColor: { rgb: lightColor }, patternType: "solid" },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border:    borderStyle,
    };

    // Data cells
    rows.forEach((row, ri) => {
      const dataRef = `${colLetter(ci)}${ri + 3}`; // +3: 1-indexed + 2 header rows
      const val = row[col.key] ?? "";
      if (!ws[dataRef]) ws[dataRef] = { v: val, t: typeof val === "number" ? "n" : "s" };
      ws[dataRef].s = {
        font:      { sz: 9, name: "Arial" },
        alignment: { vertical: "center", horizontal: isIdentity ? "left" : "center" },
        fill:      (ri % 2 === 1)
          ? { fgColor: { rgb: "F9FAFB" }, patternType: "solid" }
          : { patternType: "none" },
        border: borderStyle,
      };
    });
  });

  /* ── Freeze top 2 rows ── */
  ws["!freeze"] = { xSplit: 0, ySplit: 2, topLeftCell: "A3", activePane: "bottomLeft" };

  /* ── Workbook ── */
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Recruitment Data");

  XLSX.writeFile(wb, filename, { cellStyles: true, bookSST: false });
}